import React, { useState, useEffect } from 'react';
import { getImageUrl } from '../utils/imageUtils';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Users, UserPlus, UserMinus, Shield, Crown, MapPin, ChevronRight } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export const FollowPage = () => {
  const { api, user } = useAuth();
  const [activeTab, setActiveTab] = useState('following');
  const [following, setFollowing] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [followingTotal, setFollowingTotal] = useState(0);
  const [followersTotal, setFollowersTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'following') {
        const response = await api.get('/following');
        setFollowing(response.data.following);
        setFollowingTotal(response.data.total);
      } else {
        const response = await api.get('/followers');
        setFollowers(response.data.followers);
        setFollowersTotal(response.data.total);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleUnfollow = async (userId, pseudo) => {
    try {
      await api.delete(`/users/${userId}/follow`);
      setFollowing(prev => prev.filter(f => f.user.id !== userId));
      setFollowingTotal(prev => prev - 1);
      toast.success(`Vous ne suivez plus ${pseudo}`);
    } catch (error) {
      toast.error('Erreur lors de l\'opération');
    }
  };

  const handleFollowBack = async (userId, pseudo) => {
    try {
      await api.post(`/users/${userId}/follow`);
      setFollowers(prev => prev.map(f => 
        f.user.id === userId ? { ...f, is_following_back: true } : f
      ));
      toast.success(`Vous suivez maintenant ${pseudo}`);
    } catch (error) {
      toast.error('Erreur lors de l\'opération');
    }
  };

  const renderUserCard = (item, isFollowing = true) => {
    const { user: profile, followed_at, is_following_back } = item;
    
    return (
      <div 
        key={profile.id} 
        className="glass-card p-4 flex items-center gap-4 hover:bg-white/5 transition-colors"
        data-testid={`user-card-${profile.id}`}
      >
        <Link to={`/member/${profile.id}`} className="flex items-center gap-4 flex-1 min-w-0">
          <Avatar className="w-14 h-14 border-2 border-gold/30">
            {profile.profile_picture ? (
              <AvatarImage 
                src={getImageUrl(profile.profile_picture)} 
                alt={profile.pseudo}
              />
            ) : (
              <AvatarFallback className="bg-dark-subtle text-gold text-lg">
                {profile.pseudo?.[0]?.toUpperCase()}
              </AvatarFallback>
            )}
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-white font-medium truncate">{profile.pseudo}</span>
              {profile.is_certified && <Shield className="w-4 h-4 text-gold flex-shrink-0" />}
              {profile.is_subscribed && <Crown className="w-4 h-4 text-gold flex-shrink-0" />}
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-500">
              {profile.profile_type && (
                <span className="capitalize">{profile.profile_type}</span>
              )}
              {profile.age && <span>{profile.age} ans</span>}
              {profile.city && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {profile.city}
                </span>
              )}
            </div>
          </div>
          
          <ChevronRight className="w-5 h-5 text-gray-600 flex-shrink-0" />
        </Link>
        
        {/* Action buttons */}
        <div className="flex-shrink-0">
          {isFollowing ? (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                handleUnfollow(profile.id, profile.pseudo);
              }}
              className="btn-outline-gold text-xs flex items-center gap-1"
              data-testid={`unfollow-btn-${profile.id}`}
            >
              <UserMinus className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Ne plus suivre</span>
            </Button>
          ) : is_following_back ? (
            <span className="text-xs text-gold bg-gold/10 px-3 py-1.5 rounded-full">
              Suivi mutuel
            </span>
          ) : (
            <Button
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                handleFollowBack(profile.id, profile.pseudo);
              }}
              className="btn-gold text-xs flex items-center gap-1"
              data-testid={`followback-btn-${profile.id}`}
            >
              <UserPlus className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Suivre</span>
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-dark pt-20 md:pt-24 pb-20 md:pb-12" data-testid="follow-page">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Users className="w-8 h-8 text-gold" />
          <h1 className="font-serif text-2xl md:text-3xl gold-gradient-text">Mes Relations</h1>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('following')}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'following'
                ? 'bg-gold text-dark'
                : 'bg-dark-subtle text-gray-400 hover:bg-white/10'
            }`}
            data-testid="following-tab"
          >
            <UserPlus className="w-4 h-4" />
            <span>Suivis</span>
            <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
              activeTab === 'following' ? 'bg-dark/20 text-dark' : 'bg-white/10'
            }`}>
              {followingTotal}
            </span>
          </button>
          
          <button
            onClick={() => setActiveTab('followers')}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'followers'
                ? 'bg-gold text-dark'
                : 'bg-dark-subtle text-gray-400 hover:bg-white/10'
            }`}
            data-testid="followers-tab"
          >
            <Users className="w-4 h-4" />
            <span>Followers</span>
            <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
              activeTab === 'followers' ? 'bg-dark/20 text-dark' : 'bg-white/10'
            }`}>
              {followersTotal}
            </span>
          </button>
        </div>
        
        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-3">
            {activeTab === 'following' ? (
              following.length === 0 ? (
                <div className="glass-card p-12 text-center">
                  <UserPlus className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400 mb-2">Vous ne suivez personne</p>
                  <p className="text-gray-500 text-sm mb-4">
                    Découvrez des profils et commencez à suivre des membres
                  </p>
                  <Link to="/search" className="btn-gold inline-block">
                    Rechercher des profils
                  </Link>
                </div>
              ) : (
                following.map(item => renderUserCard(item, true))
              )
            ) : (
              followers.length === 0 ? (
                <div className="glass-card p-12 text-center">
                  <Users className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400 mb-2">Aucun followers</p>
                  <p className="text-gray-500 text-sm">
                    Partagez votre profil pour attirer des followers
                  </p>
                </div>
              ) : (
                followers.map(item => renderUserCard(item, false))
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FollowPage;
